import { Box, Button, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useState } from 'react';
import { CustomPagination, GridBox, NoRowsOverlay, TableTab } from 'src/components/Layout';
import { useAlerting, useListMappingTransform, useTranslation } from 'src/hooks';
import { downloadCsv, downloadFiles } from 'src/utils';
import { dataGridDefaults, tableConfig } from 'src/utils/constant';
import useTableColumns from '../hook/useTableColumns';
import useTableRows from '../hook/useTableRows';
import { Preview } from '../types/Preview';
import FileSelector from './FileSelector';
import { downloadTemplateJson } from '../constants/downloadTemplateJson';

const pageSize = tableConfig.pageSize;

type Props = {
  previews: Preview[];
  setPreviews: Dispatch<SetStateAction<Preview[]>>;
};

const Content = (props: Props) => {
  const { previews, setPreviews } = props;

  const { alerting } = useAlerting();

  const { t, tc } = useTranslation('clientCreditAllocation');
  const listMappingTransform = useListMappingTransform('export');

  const [page, setPage] = useState(0);
  const [fileName, setFileName] = useState<string | undefined>(undefined);

  const { rows, mapJsonDataToTupleArray } = useTableRows({ previews });
  const { columns } = useTableColumns();

  const paginatedRows = rows.slice(page * pageSize, (page + 1) * pageSize);

  const downloadTemplate = async () => {
    downloadCsv(`BatchAllocateCreditTemplate`, [downloadTemplateJson], {});
  };

  const labelElePairArr: Array<[string, JSX.Element]> = [
    [
      'downloadTemplate',
      <div
        style={{
          color: '#90D5FF',
          cursor: 'pointer',
          width: 'fit-content',
        }}
        onClick={downloadTemplate}
      >
        {'BatchAllocateCreditTemplate.csv'}
      </div>,
    ],
    [
      'uploadFile',
      <Stack>
        <FileSelector previews={previews} setPreviews={setPreviews} setFileName={setFileName} />
        {fileName && <Typography variant="body2" children={fileName} />}
      </Stack>,
    ],
  ];

  const onExport = () => {
    if (previews.length === 0) {
      return alerting('error', tc('no_data_export'));
    }
    const res = mapJsonDataToTupleArray(['rawData', 'paramData']);
    const output = res.map(listMappingTransform);
    downloadFiles('Batch_Allocate_Credit_Preview', output, {});
  };

  return (
    <Stack>
      <Box sx={{ paddingBottom: '1rem' }}>
        <GridBox labelElePairArr={labelElePairArr} columnCount={1} />
        {previews.length > 0 && (
          <Typography variant="body1" style={{ marginTop: '1rem' }}>
            {t('validation', {
              totalRecords: previews.length,
              errorRecords: previews.filter((item) => !item.validationPass).length,
            })}
          </Typography>
        )}
      </Box>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        padding={'0.5rem 0'}
      >
        <Typography variant="h6" children={t('preview')} />
        <Button color="info" variant={'outlined'} onClick={onExport} children={tc('export')} />
      </Box>
      <Stack maxHeight={'50vh'}>
        <TableTab>
          <DataGrid
            {...dataGridDefaults}
            columns={columns}
            rows={paginatedRows}
            rowCount={rows.length}
            pageSize={pageSize}
            onPageChange={(page) => setPage(page)}
            components={{
              NoRowsOverlay,
              Footer: CustomPagination,
            }}
            componentsProps={{
              footer: { totalRecords: rows.length },
            }}
          />
        </TableTab>
      </Stack>
    </Stack>
  );
};
export default Content;
